import React, { useState, useEffect, useCallback, useRef } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import apiClient from '../utils/api';
import './ConnectionsManager.css';

interface ConnectionAccount {
  plaid_account_id: string;
  account_name: string;
  balance: number;
  account_type: string;
}

interface Connection {
  id: number;
  institution_name: string | null;
  institution_id: string | null;
  institution_logo: string | null;   // base64 PNG
  institution_color: string | null;  // hex color
  accounts: ConnectionAccount[];
}

interface Props {
  onClose: () => void;
  onAccountsChanged: () => void;
}

const ConnectionsManager: React.FC<Props> = ({ onClose, onAccountsChanged }) => {
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Plaid update-mode state
  const [updateLinkToken, setUpdateLinkToken] = useState<string | null>(null);
  const editingConnectionIdRef = useRef<number | null>(null);

  // Delete confirmation state
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Expand/collapse state
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const toggleExpanded = (id: number) =>
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const loadConnections = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await apiClient.getConnections();
      setConnections(res.data.connections || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load connections.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  // ── Plaid update-mode ────────────────────────────────────────────────────
  // Update-mode tokens are per-connection so we fetch them on demand, but we
  // call openPlaidLink() directly from the edit button click once ready — the
  // same pre-fetch-then-open pattern used in Dashboard avoids the useEffect
  // race where the SDK never finishes initializing before open() is invoked.
  const [fetchingUpdateToken, setFetchingUpdateToken] = useState(false);

  const { open: openPlaidLink, ready: plaidReady } = usePlaidLink({
    token: updateLinkToken,
    onSuccess: async (_publicToken: string, _metadata: any) => {
      // Update mode does not issue a new public token — just refresh accounts
      try {
        const connId = editingConnectionIdRef.current;
        if (connId !== null) {
          await apiClient.refreshConnection(connId);
          onAccountsChanged();
          await loadConnections();
        }
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to refresh connection.');
      } finally {
        setUpdateLinkToken(null);
        editingConnectionIdRef.current = null;
      }
    },
    onExit: (err: any) => {
      setUpdateLinkToken(null);
      editingConnectionIdRef.current = null;
      if (err) setError(err.display_message || err.error_message || 'Bank connection closed with an error.');
    },
  });

  // Open Plaid Link as soon as the update token is ready.
  // We use a useEffect here specifically for the update-mode flow because
  // the token is fetched per-connection on edit click — there's no way to
  // pre-load it ahead of time like the new-connection flow in Dashboard.
  useEffect(() => {
    if (plaidReady && updateLinkToken) openPlaidLink();
  }, [plaidReady, updateLinkToken, openPlaidLink]);

  const handleEditConnection = async (connectionId: number) => {
    if (fetchingUpdateToken || updateLinkToken) return;
    try {
      setError('');
      setFetchingUpdateToken(true);
      editingConnectionIdRef.current = connectionId;
      const res = await apiClient.createUpdateLinkToken(connectionId);
      setUpdateLinkToken(res.data.link_token);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to open Plaid Link.');
      editingConnectionIdRef.current = null;
    } finally {
      setFetchingUpdateToken(false);
    }
  };

  // ── Delete connection ────────────────────────────────────────────────────
  const handleDeleteConfirm = async (connectionId: number) => {
    try {
      setDeleting(true);
      await apiClient.deleteConnection(connectionId);
      onAccountsChanged();
      await loadConnections();
      setConfirmDeleteId(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete connection.');
    } finally {
      setDeleting(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  const formatAccountType = (type: string) =>
    type ? type.charAt(0).toUpperCase() + type.slice(1).toLowerCase() : '';

  return (
    <div className="cm-backdrop" onClick={onClose}>
      <div className="cm-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="cm-header">
          <h2 className="cm-title">Manage Connections</h2>
          <button className="cm-close-btn" onClick={onClose} aria-label="Close">×</button>
        </div>

        {error && <div className="cm-error">{error}</div>}

        {/* Body */}
        <div className="cm-body">
          {loading ? (
            <div className="cm-loading">Loading connections…</div>
          ) : connections.length === 0 ? (
            <div className="cm-empty">
              No connections found. Add an account to get started.
            </div>
          ) : (
            connections.map(conn => {
              const expanded = expandedIds.has(conn.id);
              return (
              <div key={conn.id} className="cm-connection-card">
                {/* Connection header row */}
                <div
                  className="cm-conn-header cm-conn-header--clickable"
                  onClick={() => toggleExpanded(conn.id)}
                >
                  <div className="cm-conn-left">
                    <span className={`cm-chevron${expanded ? ' cm-chevron--open' : ''}`}>›</span>
                    {/* Institution logo or initials fallback */}
                    <div
                      className="cm-inst-avatar"
                      style={conn.institution_color ? { background: conn.institution_color } : undefined}
                    >
                      {conn.institution_logo ? (
                        <img
                          src={`data:image/png;base64,${conn.institution_logo}`}
                          alt={conn.institution_name || 'Bank'}
                          className="cm-inst-logo"
                        />
                      ) : (
                        <span className="cm-inst-initials">
                          {(conn.institution_name || 'B').charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="cm-conn-info">
                      <span className="cm-institution-name">
                        {conn.institution_name || 'Unknown Bank'}
                      </span>
                      <span className="cm-account-count">
                        {conn.accounts.length} account{conn.accounts.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                  <div className="cm-conn-actions" onClick={e => e.stopPropagation()}>
                    {confirmDeleteId === conn.id ? (
                      <span className="cm-confirm-delete">
                        Remove?&nbsp;
                        <button
                          className="cm-btn-yes"
                          onClick={() => handleDeleteConfirm(conn.id)}
                          disabled={deleting}
                        >
                          Yes
                        </button>
                        <button
                          className="cm-btn-cancel"
                          onClick={() => setConfirmDeleteId(null)}
                          disabled={deleting}
                        >
                          Cancel
                        </button>
                      </span>
                    ) : (
                      <>
                        <button
                          className="cm-icon-btn cm-edit-btn"
                          title="Edit connection in Plaid"
                          onClick={() => handleEditConnection(conn.id)}
                        >
                          ✏️
                        </button>
                        <button
                          className="cm-icon-btn cm-delete-btn"
                          title="Remove connection"
                          onClick={() => setConfirmDeleteId(conn.id)}
                        >
                          🗑️
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Accounts sub-list */}
                {expanded && conn.accounts.length > 0 && (
                  <ul className="cm-accounts-list">
                    {conn.accounts.map(acc => (
                      <li key={acc.plaid_account_id} className="cm-account-row">
                        <div className="cm-account-main">
                          <span className="cm-account-name">{acc.account_name}</span>
                          <span className="cm-account-type-badge">
                            {formatAccountType(acc.account_type)}
                          </span>
                        </div>
                        <span className="cm-account-balance">
                          ${acc.balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default ConnectionsManager;
